import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    excluirFaixasDistancia,
    obterFaixasDistancia,
    salvarFaixasDistancia,
} from "../../../services/faixasDistancia"
import { getIdEstabelecimentoAtual } from "../../../services/quitacaoAutomatica"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import {
    AtribuirFaixasDistanciaInicial,
    FaixaDistancia,
} from "../models/atribuirFaixasDistanciaModel"
import { AtribuirFaixasDistanciaActionsTypes } from "./atribuirFaixasDistanciaActions"
import { AtribuirFaixasDistanciaContext } from "./AtribuirFaixasDistanciaContext"
import { atribuirFaixasDistanciaReducer } from "./atribuirFaixasDistanciaReducer"

export function AtribuirFaixasDistanciaContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const [faixas, dispatch] = useReducer(
        atribuirFaixasDistanciaReducer,
        AtribuirFaixasDistanciaInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)
    const configuracoes = useAppStore(s => s.configuracoes)
    // Loja SELECIONADA no seletor (estabelecimentoAtualCF); parametros.estabelecimento
    // aponta para a loja do usuario logado e nao reflete a troca de loja.
    const idEstabelecimento =
        getIdEstabelecimentoAtual() || configuracoes?.user?.parametros?.estabelecimento?.id

    const carregar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento) return

        try {
            setLoading(true)
            const response = await obterFaixasDistancia(autorizacao, idEstabelecimento)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar faixas de distância")
            } else {
                setErroCarregamento(null)
                dispatch({ type: AtribuirFaixasDistanciaActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar faixas de distância")
        } finally {
            setLoading(false)
        }
    }, [idEstabelecimento])

    useEffect(() => {
        carregar()
    }, [carregar])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !idEstabelecimento) return

        setLoading(true)
        try {
            const response = await salvarFaixasDistancia(autorizacao, idEstabelecimento, faixas)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({ type: AtribuirFaixasDistanciaActionsTypes.CARREGAR, payload: response })
        } finally {
            setLoading(false)
        }
    }, [faixas, idEstabelecimento])

    const removerFaixa = useCallback(
        async (index: number, faixa: FaixaDistancia) => {
            if (faixa.id !== null && faixa.id > 0) {
                const autorizacao = localStorage.getItem("chaveSistema")
                if (!autorizacao || !idEstabelecimento) return

                setLoading(true)
                try {
                    const response = await excluirFaixasDistancia(autorizacao, idEstabelecimento, [
                        faixa,
                    ])
                    if ("retornoErro" in response) {
                        throw new Error(response.mensagem || "Erro ao remover faixa")
                    }
                } finally {
                    setLoading(false)
                }
            }
            dispatch({
                type: AtribuirFaixasDistanciaActionsTypes.REMOVER_FAIXA,
                payload: { index },
            })
        },
        [idEstabelecimento]
    )

    return (
        <AtribuirFaixasDistanciaContext.Provider
            value={{ faixas, dispatch, salvar, removerFaixa, erroCarregamento }}
        >
            <Loading />
            {children}
        </AtribuirFaixasDistanciaContext.Provider>
    )
}
